import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Calendar,
  RefreshCw,
  Eye,
  AlertCircle,
  Package
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useStore } from '../utils/StoreContext';
import { AnalyticsService, MovementAnalytics } from '../services/analyticsService';

interface MovementTrackerWidgetProps {
  onCategoryClick?: (category: string) => void;
  className?: string;
}

const MovementTrackerWidget: React.FC<MovementTrackerWidgetProps> = ({
  onCategoryClick,
  className = ''
}) => {
  const { user } = useAuth();
  const { currentStore } = useStore();
  
  const [analytics, setAnalytics] = useState<MovementAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState(() => AnalyticsService.getDefaultDateRange());

  // Load analytics data
  const loadAnalytics = async () => {
    if (!user?.id || !currentStore?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      const data = await AnalyticsService.getItemMovementAnalytics(
        user.id,
        currentStore.id,
        dateRange.startDate,
        dateRange.endDate
      );
      
      setAnalytics(data);
    } catch (err) {
      console.error('Error loading movement analytics:', err);
      setError('Failed to load movement analytics');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when dependencies change
  useEffect(() => {
    loadAnalytics();
  }, [user?.id, currentStore?.id, dateRange]);

  // Handle date range change
  const handleDateRangeChange = (days: number) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    setDateRange({
      startDate: AnalyticsService.formatDate(startDate),
      endDate: AnalyticsService.formatDate(endDate)
    });
  };

  // Handle category click
  const handleCategoryClick = (category: string) => {
    if (onCategoryClick) {
      onCategoryClick(category);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Item Movement Tracker</h3>
          <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="h-32 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-red-200 p-6 ${className}`}>
        <div className="flex items-center space-x-2 text-red-600 mb-2">
          <AlertCircle className="w-5 h-5" />
          <h3 className="text-lg font-semibold">Error Loading Analytics</h3>
        </div>
        <p className="text-red-600 text-sm mb-4">{error}</p>
        <button
          onClick={loadAnalytics}
          className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const { summary } = analytics;
  
  // Calculate bar widths for visualization
  const maxPercentage = Math.max(
    summary.no_movement_percentage,
    summary.slow_moving_percentage,
    summary.medium_moving_percentage,
    summary.fast_moving_percentage
  );

  const categories = [
    {
      key: 'no_movement',
      name: 'No Movement',
      count: summary.no_movement_items,
      percentage: summary.no_movement_percentage,
      color: 'bg-red-500',
      lightColor: 'bg-red-100',
      textColor: 'text-red-700',
      icon: AlertCircle
    },
    {
      key: 'slow_moving',
      name: 'Slow Moving',
      count: summary.slow_moving_items,
      percentage: summary.slow_moving_percentage,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-100',
      textColor: 'text-orange-700',
      icon: TrendingDown
    },
    {
      key: 'medium_moving',
      name: 'Medium Moving',
      count: summary.medium_moving_items,
      percentage: summary.medium_moving_percentage,
      color: 'bg-yellow-500',
      lightColor: 'bg-yellow-100',
      textColor: 'text-yellow-700',
      icon: Activity
    },
    {
      key: 'fast_moving',
      name: 'Fast Moving',
      count: summary.fast_moving_items,
      percentage: summary.fast_moving_percentage,
      color: 'bg-green-500',
      lightColor: 'bg-green-100',
      textColor: 'text-green-700',
      icon: TrendingUp
    }
  ];

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Package className="w-5 h-5" />
            <span>Item Movement Tracker</span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Analysis of {summary.total_items} items from {new Date(analytics.date_range.start_date).toLocaleDateString()} to {new Date(analytics.date_range.end_date).toLocaleDateString()}
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh data"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Date Range Selector */}
      <div className="flex items-center space-x-2 mb-6">
        <Calendar className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">Period:</span>
        <div className="flex space-x-1">
          {[30, 60, 90].map((days) => (
            <button
              key={days}
              onClick={() => handleDateRangeChange(days)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                dateRange.startDate === AnalyticsService.formatDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000))
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* Single Movement Distribution Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900">Movement Distribution</h4>
          <span className="text-xs text-gray-500">{summary.total_items} total items</span>
        </div>
        
        {/* Single Combined Bar */}
        <div className="relative h-8 bg-gray-200 rounded-lg overflow-hidden shadow-inner">
          {categories.map((category, index) => {
            const IconComponent = category.icon;
            let leftPosition = 0;
            
            // Calculate cumulative position
            for (let i = 0; i < index; i++) {
              leftPosition += categories[i].percentage;
            }
            
            return category.percentage > 0 ? (
              <div
                key={category.key}
                className={`absolute top-0 h-full ${category.color} cursor-pointer transition-all duration-500 ease-out group hover:brightness-110`}
                style={{ 
                  left: `${leftPosition}%`, 
                  width: `${category.percentage}%` 
                }}
                onClick={() => handleCategoryClick(category.key)}
                title={`${category.name}: ${category.count} items (${category.percentage}%)`}
              >
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                
                {/* Category label for larger segments */}
                {category.percentage > 8 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white text-xs font-medium drop-shadow-sm">
                      {category.percentage}%
                    </span>
                  </div>
                )}
              </div>
            ) : null;
          })}
        </div>
        
        {/* Legend */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <button
                key={category.key}
                onClick={() => handleCategoryClick(category.key)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className={`w-3 h-3 rounded-sm ${category.color}`} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center space-x-1">
                    <IconComponent className={`w-3 h-3 ${category.textColor}`} />
                    <span className="text-xs font-medium text-gray-900 truncate">
                      {category.name}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {category.count} items ({category.percentage}%)
                  </div>
                </div>
                {onCategoryClick && (
                  <Eye className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">{summary.total_items}</div>
            <div className="text-xs text-gray-500">Total Items</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">
              {(summary.no_movement_percentage + summary.slow_moving_percentage).toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500">Needs Attention</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovementTrackerWidget;
